import Feature from 'ol/Feature';
import {Point} from 'ol/geom';
import {Circle as CircleStyle, Fill, Icon, Stroke, Style} from 'ol/style';
import {fromLonLat} from 'ol/proj';
import {hexToRGBA} from "@/assets/js/utils";
import {explosion} from "@/animations/effects/explosion";

/**
 * Airstrike animation — a fighter jet flies in across the target, leaves the
 * scene, and the strike lands behind it as a wave of explosion bursts (each
 * reusing the shared explosion effect).
 *
 * Expected `data`: { "center": [lon, lat], "spread": 1200, "approach": 5000, "speed": 3 }
 *   `spread`   explosion scatter radius around target, EPSG:3857 metres (~1km default)
 *   `approach` distance the jet travels each side of the target, metres (default 5000)
 *   `speed`    jet speed; flyover time ≈ (2*approach)/speed ms (default 3 → ~3.3s)
 */
export function airstrike(layer, news, data) {
    const target = fromLonLat(data.center);
    const spread = data.spread || 1200;
    const approach = data.approach || 5000;
    const speed = data.speed || 3;

    const parent = new Feature({
        geometry: new Point(target),
        where: target
    });
    parent.setStyle([
        new Style({
            image: new CircleStyle({
                radius: 10,
                stroke: new Stroke({color: news.color, width: 2}),
                fill: new Fill({color: hexToRGBA(news.color, 0.15)})
            })
        }),
        new Style({
            image: new CircleStyle({
                radius: 3,
                fill: new Fill({color: news.color})
            })
        })
    ]);
    layer.getSource().addFeature(parent);

    function spawnExplosion() {
        const offsetX = (Math.random() - 0.5) * spread;
        const offsetY = (Math.random() - 0.5) * spread;
        explosion(layer, [target[0] + offsetX, target[1] + offsetY], {
            color: news.color,
            maxRadius: 12,
            fire: Math.random() < 0.35,
            fireCount: 3,
            fireSpread: 400,
            fireDuration: 2200
        });
    }

    function burstWave() {
        const count = 4 + Math.floor(Math.random() * 4);
        for (let i = 0; i < count; i++) {
            setTimeout(spawnExplosion, i * 180 + Math.random() * 120);
        }
    }

    function jetStyle(rotation) {
        return new Style({
            image: new Icon({
                src: '/images/jet.svg',
                scale: 0.5,
                rotation,
                rotateWithView: true
            })
        });
    }

    const FLY_TICK = 40;
    function sortie() {
        const angle = Math.random() * Math.PI * 2;
        const dir = [Math.cos(angle), Math.sin(angle)];
        const start = [target[0] - dir[0] * approach, target[1] - dir[1] * approach];
        const end = [target[0] + dir[0] * approach, target[1] + dir[1] * approach];

        const jet = new Feature(new Point(start));
        jet.set('selectable', false);
        jet.setStyle(jetStyle(Math.atan2(dir[0], dir[1])));
        layer.getSource().addFeature(jet);

        const duration = (2 * approach) / speed;
        let elapsed = 0;
        const interval = setInterval(() => {
            elapsed += FLY_TICK;
            const p = elapsed / duration;
            if (p >= 1) {
                clearInterval(interval);
                layer.getSource().removeFeature(jet);
                burstWave();
                return;
            }
            jet.setGeometry(new Point([
                start[0] + (end[0] - start[0]) * p,
                start[1] + (end[1] - start[1]) * p
            ]));
        }, FLY_TICK);
    }

    sortie();
    setInterval(sortie, 4000);

    return {
        getFeature() {
            return parent;
        }
    };
}
